import { useEffect, useRef } from 'react';
import { withWindow } from '@shared/utils/withWindow';
import { useUnlockDialogContext } from './UnlockDialogProvider';

/**
 * Hook that defers input focus after drawer animation completes.
 *
 * iOS Safari/Chrome doesn't trigger native keyboard scroll behavior for synchronous
 * programmatic focus (like `enableFocusOnMount`). By deferring the focus with
 * a delay after the drawer animation, iOS treats it more like a user-initiated focus
 * and correctly scrolls the input into view above the keyboard.
 *
 * Uses the dialog's isOpen state to re-trigger focus each time the dialog opens.
 */
export const useDeferredInputFocus = <T extends HTMLElement = HTMLInputElement>() => {
  const ref = useRef<T>(null);
  const { isOpen } = useUnlockDialogContext();

  useEffect(() => {
    if (!isOpen) return;

    // Wait for drawer animation to complete before focusing
    const timer = withWindow(
      global =>
        global.setTimeout(() => {
          ref.current?.focus();
        }, 300),
      undefined,
    );
    return () => withWindow(global => global.clearTimeout(timer));
  }, [isOpen]);

  return ref;
};
